interface DeepThinkingToggleProps {
  enabled: boolean;
  onChange: (enabled: boolean) => void;
  visible: boolean;
  disabled: boolean;
  disabledReason?: string;
}

export default function DeepThinkingToggle({
  enabled,
  onChange,
  visible,
  disabled,
  disabledReason,
}: DeepThinkingToggleProps) {
  if (!visible) return null;

  return (
    <div className="group/dt relative flex items-center gap-2">
      <button
        type="button"
        role="switch"
        aria-checked={enabled}
        aria-disabled={disabled}
        disabled={disabled}
        onClick={() => onChange(!enabled)}
        className={`
          relative inline-flex h-6 w-11 shrink-0 rounded-full border-2 border-transparent
          transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2
          focus:ring-primary focus:ring-offset-2
          ${enabled ? 'bg-primary' : 'bg-border'}
          ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}
        `}
      >
        <span
          className={`
            pointer-events-none inline-block h-5 w-5 rounded-full bg-bg shadow-sm
            ring-0 transition-transform duration-200 ease-in-out
            ${enabled ? 'translate-x-5' : 'translate-x-0'}
          `}
        />
      </button>

      <div className="flex items-center gap-1.5">
        {/* Brain icon */}
        <svg
          className={`h-4 w-4 transition-colors ${enabled ? 'text-primary' : 'text-text-muted'}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
          />
        </svg>
        <span
          className={`text-sm font-medium ${enabled ? 'text-primary' : 'text-text-secondary'}`}
        >
          Deep Thinking
        </span>
      </div>

      {/* Tooltip when disabled */}
      {disabled && disabledReason && (
        <div
          role="tooltip"
          className="
            pointer-events-none absolute bottom-full left-1/2 mb-2 -translate-x-1/2
            whitespace-nowrap rounded-md bg-text-primary px-3 py-1.5
            text-xs text-bg opacity-0 shadow-md
            transition-opacity duration-150 group-hover/dt:opacity-100
          "
        >
          {disabledReason}
          <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-text-primary" />
        </div>
      )}
    </div>
  );
}
